/**
 * Ask before an action that cannot be taken back — the ONE way the app does it.
 *
 *   const { confirm, confirmDialog } = useConfirm();
 *   …
 *   <button aria-label={`Delete ${w.name}`} onClick={async () => {
 *     if (await confirm({
 *       title: `Delete "${w.name}"?`,
 *       message: 'It stops running on its trigger. This cannot be undone.',
 *       confirmLabel: 'Delete workflow',
 *     })) removeWorkflow.mutate(w.id);
 *   }}>…</button>
 *   {confirmDialog}
 *
 * Deep-test ADM-031 / DSH-017: the workflow and report bin icons deleted on one
 * click, and a sweep found the same shape on a dozen more screens, next to
 * seven `window.confirm` calls and a handful of hand-rolled dialogs. Each was a
 * page's own answer to the same question. This hook is the shared answer: it
 * renders the existing ConfirmDialog (components/Common/ConfirmDialog.tsx), so
 * every confirmation looks and behaves the same, and `confirm()` returns a
 * promise, so a one-click handler becomes a confirmed one by adding a single
 * `if (await confirm(...))` — no target state, no second component per page.
 *
 * It is SELF-CONTAINED rather than a context provider on purpose: the hook owns
 * its dialog and the page renders `{confirmDialog}`, so a page rendered alone
 * in a test (or in any future shell) behaves the same as in the app, and there
 * is no provider to forget. `deletesAskFirst.test.ts` checks both halves — every
 * DELETE behind a confirm, and every useConfirm page rendering its dialog.
 *
 * The message should NAME what is being lost ("Delete "Weekly occupancy"?"),
 * not "Are you sure?" — a dialog that does not say what it deletes is one the
 * user learns to click through.
 */
import { useCallback, useRef, useState } from 'react';
import ConfirmDialog from '@/components/Common/ConfirmDialog';

export interface ConfirmOptions {
  title: string;
  /** What happens, in words: what is removed and what it stops doing. */
  message: string;
  confirmLabel?: string;
  cancelLabel?: string;
  variant?: 'danger' | 'warning' | 'info';
}

export function useConfirm() {
  // The options outlive `open` so the dialog keeps its words while it fades.
  const [open, setOpen] = useState(false);
  const [pending, setPending] = useState<ConfirmOptions | null>(null);
  const resolver = useRef<((ok: boolean) => void) | null>(null);

  const settle = useCallback((ok: boolean) => {
    resolver.current?.(ok);
    resolver.current = null;
    setOpen(false);
  }, []);

  const confirm = useCallback((opts: ConfirmOptions) => {
    // A second ask while one is open answers the first "no" rather than
    // leaving its promise hanging for ever.
    resolver.current?.(false);
    setPending(opts);
    setOpen(true);
    return new Promise<boolean>((resolve) => { resolver.current = resolve; });
  }, []);

  const confirmDialog = (
    <ConfirmDialog
      isOpen={open}
      onClose={() => settle(false)}
      onConfirm={() => settle(true)}
      title={pending?.title ?? ''}
      message={pending?.message ?? ''}
      confirmLabel={pending?.confirmLabel ?? 'Delete'}
      cancelLabel={pending?.cancelLabel ?? 'Cancel'}
      variant={pending?.variant ?? 'danger'}
    />
  );

  return { confirm, confirmDialog };
}
